import Image from 'next/image'
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

interface OurLocationData {
  heading?: {
    text?: string;
    title?: string;
  };
  address_info?: {
    text?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    href?: string;
    label?: string;
  };
  background_image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
}

export const fields = [
  "heading.text",
  "heading.title",
  "address_info.text",
  "text.text",
  "button.href",
  "button.label",
  "background_image.alt",
  "background_image.image_file.image.id",
  "background_image.image_file.image.filename_download",
] as const;

interface OurLocationProps {
  data?: OurLocationData;
}

export default function OurLocation({ data }: OurLocationProps) {
  const imageId = data?.background_image?.image_file?.image?.id || "";
  const imageFilename = data?.background_image?.image_file?.image?.filename_download || "";
  const imageAlt = data?.background_image?.alt || "Our location";
  const imageUrl = imageId ? getReadableImageUrl(imageId, imageFilename) : "/images/our-location.png";

  return (
    <section className="relative w-full overflow-hidden bg-[#F6F6F6]">
      {/* Desktop and Mobile Hero Section */}
      <div className="mx-auto pt-8 pb-8 xl:pb-16">
        <div className="relative lg:flex lg:items-center">
          {/* Image Container */}
          <div className="relative w-full lg:w-3/5 lg:order-2 desktopFadeFromRight">
            <div className="relative h-[30.5625rem] lg:h-[40rem] overflow-hidden">
              <div className="absolute right-0 w-full h-full lg:rounded-tl-[25rem] lg:rounded-bl-[25rem] overflow-hidden">
                <Image
                  src={imageUrl}
                  alt={imageAlt}
                  fill
                  className="object-cover object-center"
                  priority
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={90}
                />
              </div>
            </div>
          </div>


          {/* Content Container */}
          <div className="relative px-5 lg:px-0 lg:w-2/5 lg:pl-28 lg:pr-20 z-10 -mt-[4rem] lg:mt-0">
            <div className="absolute -top-0 left-0 w-full lg:hidden -z-10">
              <Image
                src="/images/top-ellipse.svg"
                alt="Decorative top shape"
                width={393}
                height={39}
                className="w-full h-[5rem] object-cover"
                quality={90}
              />
            </div>

            <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mt-8 pt-16 lg:pt-0">
              {data?.heading?.text || data?.heading?.title}
            </h2>

            <div 
              className='text-2xl font-greycliff mt-16 text-primary [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:text-2xl'
              dangerouslySetInnerHTML={sanitizeHTML(data?.address_info?.text || "")}
            />

            <div 
              className="text-primary font-greycliff mt-8 tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:text-xl"
              dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
            />

            <Link 
              href={data?.button?.href || "#"}
              className="inline-block mt-6 lg:mt-11 w-full lg:w-auto px-12 py-4 bg-primary text-white rounded-full font-greycliff text-xl tracking-tight hover:bg-[#122947] transition-colors text-center"
            >
              {data?.button?.label}
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
} 